import React, { useCallback, useMemo, useRef } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';

import { RightArrowSettings } from 'assets/images';

import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import {
    selectPlayerAudio,
    selectPlayerSubtitles,
    selectSubtitlesFontSize,
} from 'store/player/selectors';
import { SELECTED_OPTION_TO_TRANSLATION } from 'store/player/utils';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isWeb } from 'utils/responsiveness';

import { SUBTITLES_FONT_SIZE_OPTIONS } from 'screens/Settings/constants.ts';

import SettingsRowItem from '../components/RowItem';
import WebHeader from '../components/WebHeader';
import {
    SETTINGS_AUDIO_LANGUAGE,
    SETTINGS_AUDIO_SUBTITLE_FONT_SIZE,
    SETTINGS_LANGUAGE_AUDIO_SUBTITLES,
    SETTINGS_SUBTITLE_LANGUAGE,
} from '../languagesConst';
import { SimpleSettingsItem } from '../types';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.SETTINGS>;
};

function AudioSubtitles({ navigation }: Props) {
    const { t } = useTranslation();

    const { background } = useSelector(selectLayoutConfig);
    const selectedAudio = useSelector(selectPlayerAudio);
    const selectedSubtitles = useSelector(selectPlayerSubtitles);
    const selectedSubtitlesFontSize = useSelector(selectSubtitlesFontSize);

    useHeaderConfig({
        isRoot: isWeb,
        disableFocusOnMount: true,
    });

    const listStyle = useMemo(
        () => ({
            backgroundColor: background,
        }),
        [background],
    );
    const menu = useMemo<SimpleSettingsItem[]>(() => {
        const _menu = [
            {
                id: SETTINGS_AUDIO_LANGUAGE,
                title: SETTINGS_AUDIO_LANGUAGE,
                subtitle: SELECTED_OPTION_TO_TRANSLATION[selectedAudio],
            },
            {
                id: SETTINGS_SUBTITLE_LANGUAGE,
                title: SETTINGS_SUBTITLE_LANGUAGE,
                subtitle: SELECTED_OPTION_TO_TRANSLATION[selectedSubtitles],
            },
            {
                id: SETTINGS_AUDIO_SUBTITLE_FONT_SIZE,
                title: SETTINGS_AUDIO_SUBTITLE_FONT_SIZE,
                subtitle:
                    SUBTITLES_FONT_SIZE_OPTIONS.find(
                        ({ id }) => selectedSubtitlesFontSize === id,
                    )?.title || '',
            },
        ];

        return _menu;
    }, [
        selectedAudio,
        selectedSubtitles,
        selectedSubtitlesFontSize,
        navigation,
    ]);

    const handleMenuItemPress = useCallback(
        ({ id }: SimpleSettingsItem) => {
            switch (id) {
                case SETTINGS_AUDIO_LANGUAGE:
                    navigation.navigate(SCREEN.SETTINGS_AUDIO);
                    break;
                case SETTINGS_SUBTITLE_LANGUAGE:
                    navigation.navigate(SCREEN.SETTINGS_SUBTITLES);
                    break;
                case SETTINGS_AUDIO_SUBTITLE_FONT_SIZE:
                    navigation.navigate(SCREEN.SETTINGS_SUBTITLES_FONT_SIZE);
                    break;
                default:
                    break;
            }
        },
        [navigation],
    );

    const debouncedMenuItemPress = useRef(
        debounce(handleMenuItemPress, 500, {
            leading: true,
            trailing: false,
        }),
    );

    return (
        <View style={[gStyles.settingsContainer, listStyle]}>
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_LANGUAGE_AUDIO_SUBTITLES)}
                    disableFocusOnMount
                />
            )}
            <View
                style={gStyles.settingsContentContainer}
                accessibilityRole="menu"
            >
                {menu.map((item, index) => (
                    <SettingsRowItem
                        key={item.id}
                        item={item}
                        title={t(item.title)}
                        subtitle={t(item.subtitle!)}
                        onPress={debouncedMenuItemPress.current}
                        RightIcon={RightArrowSettings}
                        position={index + 1}
                        total={menu.length}
                    />
                ))}
            </View>
        </View>
    );
}

export default AudioSubtitles;
